import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@chakra-ui/icons';

import { AddCircleOutline, useToast } from '@aslanpay/portal-common-lib';

import useUploadSavingsPresetImage from 'app/hooks/useUploadSavingsPresetImage';

import {
  ALLOWED_SAVINGS_PRESET_IMAGE_EXTENSIONS,
  isAllowedSavingsPresetImage,
  isWithinSavingsPresetImageSize,
} from 'app/utils/savingsPresetUpload';

import PresetThumbnail from './PresetThumbnail';
import { CategoryImagesModalProps } from './types';

const ACCEPTED_FILE_INPUT = ALLOWED_SAVINGS_PRESET_IMAGE_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(',');

/**
 * Editor for a category's images: a single ordered list. Images are uploaded via drag-and-drop or
 * the picker (multiple at once), the chevrons reorder them, and the ✕ removes them. Every add /
 * reorder / remove is persisted to the category immediately.
 */
function CategoryImagesModal({
  isOpen,
  category,
  presetImages,
  isSaving,
  onClose,
  onImagesChange,
}: CategoryImagesModalProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageIds, setImageIds] = useState<string[]>([]);
  // Mirrors imageIds synchronously so async uploads / rapid reorder-remove act on the latest list,
  // never a stale render snapshot.
  const imageIdsRef = useRef<string[]>([]);
  // Names for images uploaded in this session, so their thumbnails resolve before the catalogue refetches.
  const [uploadedNames, setUploadedNames] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const ordered = [...category.images]
        .sort((first, second) => first.displayOrder - second.displayOrder)
        .map((image) => image.imageId);
      imageIdsRef.current = ordered;
      setImageIds(ordered);
      setUploadedNames({});
    }
  }, [isOpen, category]);

  const nameById = useMemo(
    () => new Map(presetImages.map((preset) => [preset.id, preset.name])),
    [presetImages]
  );

  const resolveName = (imageId: string) => uploadedNames[imageId] ?? nameById.get(imageId);

  const upload = useUploadSavingsPresetImage();

  // Apply a new ordered set locally (snappy) and persist it immediately.
  const applyImages = (next: string[]) => {
    imageIdsRef.current = next;
    setImageIds(next);
    onImagesChange(next);
  };

  const moveImage = (index: number, direction: -1 | 1) => {
    const { current } = imageIdsRef;
    const target = index + direction;
    if (target < 0 || target >= current.length) {
      return;
    }
    const reordered = [...current];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    applyImages(reordered);
  };

  const removeImage = (imageId: string) => {
    applyImages(imageIdsRef.current.filter((id) => id !== imageId));
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const valid = files.filter(
      (file) => isAllowedSavingsPresetImage(file) && isWithinSavingsPresetImageSize(file)
    );
    const skipped = files.length - valid.length;

    if (skipped > 0) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Some files skipped',
        description: `${skipped} file(s) skipped — only PNG or JPG images up to 5 MB are allowed.`,
      });
    }

    if (valid.length === 0) {
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await Promise.all(
        valid.map(async (file) => ({ id: await upload.mutateAsync(file), name: file.name }))
      );
      setUploadedNames((current) => ({
        ...current,
        ...Object.fromEntries(uploaded.map((item) => [item.id, item.name])),
      }));
      // Read the latest list (uploads may have overlapped reorder/remove), not the stale snapshot.
      const { current } = imageIdsRef;
      const newIds = uploaded.map((item) => item.id).filter((id) => !current.includes(id));
      if (newIds.length > 0) {
        applyImages([...current, ...newIds]);
      }
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Upload failed',
        description: 'One or more images could not be uploaded. Please try again.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    uploadFiles(files);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    uploadFiles(Array.from(event.dataTransfer.files));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Images — {category.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Center
            role="button"
            tabIndex={0}
            flexDir="column"
            gap="6px"
            w="full"
            py="24px"
            mb="16px"
            border="2px dashed"
            borderColor={isDragging ? 'brand.purple' : 'brand.lightGray1'}
            bg={isDragging ? 'brand.lightPurple' : 'transparent'}
            borderRadius="10px"
            cursor="pointer"
            aria-label="Upload images"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            data-testid="image-dropzone"
          >
            {isUploading ? (
              <Spinner color="brand.purple" size="md" />
            ) : (
              <AddCircleOutline boxSize="24px" color="brand.purple" />
            )}
            <Text textStyle="smallMedium" color="brand.black">
              {isUploading ? 'Uploading…' : 'Drag & drop images here, or click to upload'}
            </Text>
            <Text textStyle="extraSmallRegular" color="brand.gray">
              PNG or JPG, up to 5 MB each
            </Text>
          </Center>

          <Text textStyle="smallSemiBold" color="brand.darkPurple" mb="8px">
            {imageIds.length} {imageIds.length === 1 ? 'image' : 'images'}
          </Text>

          {imageIds.length === 0 ? (
            <Text textStyle="smallRegular" color="brand.gray">
              No images yet.
            </Text>
          ) : (
            <Flex direction="column" gap="8px">
              {imageIds.map((imageId, index) => (
                <Flex
                  key={imageId}
                  align="center"
                  gap="12px"
                  p="8px"
                  border="1px solid"
                  borderColor="brand.lightGray1"
                  borderRadius="8px"
                  data-testid="category-image-row"
                >
                  {imageIds.length > 1 && (
                    <Flex direction="column" gap="6px">
                      {index > 0 && (
                        <IconButton
                          aria-label={`Move ${resolveName(imageId) ?? 'image'} up`}
                          icon={<ChevronUpIcon boxSize="18px" />}
                          variant="ghost"
                          size="xs"
                          onClick={() => moveImage(index, -1)}
                        />
                      )}
                      {index < imageIds.length - 1 && (
                        <IconButton
                          aria-label={`Move ${resolveName(imageId) ?? 'image'} down`}
                          icon={<ChevronDownIcon boxSize="18px" />}
                          variant="ghost"
                          size="xs"
                          onClick={() => moveImage(index, 1)}
                        />
                      )}
                    </Flex>
                  )}
                  <PresetThumbnail imageId={imageId} fileName={resolveName(imageId)} size="48px" />
                  <Text flex="1" minW={0} textStyle="smallMedium" color="brand.black" noOfLines={1}>
                    {resolveName(imageId) ?? 'Image'}
                  </Text>
                  <IconButton
                    aria-label={`Remove ${resolveName(imageId) ?? 'image'}`}
                    icon={<CloseIcon boxSize="10px" />}
                    variant="ghost"
                    minW="32px"
                    w="32px"
                    h="32px"
                    p="0"
                    borderRadius="full"
                    color="system.error"
                    _hover={{ bg: 'red.100' }}
                    onClick={() => removeImage(imageId)}
                  />
                </Flex>
              ))}
            </Flex>
          )}

          <Input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_INPUT}
            display="none"
            onChange={handleInputChange}
            data-testid="category-image-upload-input"
          />
        </ModalBody>
        <ModalFooter>
          {isSaving && (
            <Text mr="auto" textStyle="smallRegular" color="brand.gray">
              Saving…
            </Text>
          )}
          <Button onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CategoryImagesModal;
